/**
 * Route path constants — single source of truth for all application routes.
 * Import ROUTES anywhere you need to navigate or compare against a path.
 */

export const ROUTES = {
  // Public
  LOGIN: '/login',
  UNAUTHORIZED: '/unauthorized',

  // Entra auth flow
  AUTH_CALLBACK: '/auth/callback',
  AUTH_ORG_SELECT: '/auth/org-select',

  // Auth-gated utility
  ONBOARDING: '/onboarding',

  // Main app
  DASHBOARD: '/dashboard',
  SETTINGS: '/settings',

  // Users / Roles / Permissions / Groups
  USERS: '/users',
  TEAM: '/team',
  ROLES: '/roles',
  PERMISSIONS: '/permissions',
  GROUPS: '/groups',

  // Tasks
  MY_TASKS: '/my-tasks',
  TASKS: '/tasks',

  // Locations / Clients
  LOCATIONS: '/locations',
  CLIENTS: '/clients',
  GOALS: '/goals',

  // Support (In-App Support & Help Center) — the queues live inside the
  // Action Centers; these constants point at the canonical section routes.
  SUPPORT_QUEUE: '/action-center',
  PLATFORM_SUPPORT: '/platform/action-center',
  HELP: '/help',
  HELP_RELEASE_NOTES: '/help/release-notes',
  HELP_COMMON_QUESTIONS: '/help/common-questions',

  // Admin
  ADMIN_PLANS: '/admin/plans',
  ADMIN_FILE_ASSETS: '/admin/file-assets',

  // Platform (super admin)
  PLATFORM: '/platform',
  PLATFORM_ACTION_CENTER: '/platform/action-center',
  PLATFORM_ORGS: '/platform/organizations',
  PLATFORM_USERS: '/platform/users',
  PLATFORM_SUBSCRIPTIONS: '/platform/subscriptions',
  PLATFORM_INVOICES: '/platform/invoices',
  PLATFORM_EMAIL_LOGS: '/platform/email-logs',
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

// ── Dynamic path builders ────────────────────────────────────────────────
// Route params (`:articleId`) aren't representable in the ROUTES map above —
// build the concrete path here once so callers never hand-concatenate it.

/**
 * Full-page reading experience for a single help article.
 * Note: `release-notes` and `common-questions` are reserved segments under
 * /help (see HELP_RELEASE_NOTES / HELP_COMMON_QUESTIONS) and must never be
 * used as article ids.
 */
export const helpArticlePath = (articleId: string) => `${ROUTES.HELP}/${articleId}`;

/** Help Center index, preselected to a given collection. */
export const helpCollectionPath = (collectionId: string) => `${ROUTES.HELP}?collection=${collectionId}`;
